// `Intl.NumberFormat` construction is comparatively expensive, so one instance
// is kept per decimal count rather than built on every call.
const numberFormats = new Map<number, Intl.NumberFormat>();

const getNumberFormat = (decimals: number) => {
  const cached = numberFormats.get(decimals);
  if (cached) {
    return cached;
  }

  const numberFormat = new Intl.NumberFormat('nb-NO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  numberFormats.set(decimals, numberFormat);
  return numberFormat;
};

export interface FormatAmountOptions {
  /** Appended after the number, e.g. `kr` or `NOK`. */
  suffix?: string;
  /** Prefix non-negative values with an explicit `+` (negatives keep their own `-`). */
  signed?: boolean;
  /**
   * Fixed number of decimals to render, padded with zeroes when the value has
   * fewer. Defaults to `0`, which rounds to whole units.
   */
  decimals?: number;
}

export const formatAmount = (
  value: number,
  { suffix = '', signed = false, decimals = 0 }: FormatAmountOptions = {},
) => {
  const number = getNumberFormat(decimals).format(value);
  // `-0 >= 0` is true, but Intl renders it with a minus sign — so the plus must
  // be withheld, or the output would carry both signs (`+ −0`).
  const isNonNegative = value >= 0 && !Object.is(value, -0);
  const formatted = signed && isNonNegative ? `+ ${number}` : number;
  return suffix ? `${formatted} ${suffix}` : formatted;
};

/**
 * Builds a `formatAmount` with pre-bound options, so call sites don't repeat
 * them:
 *
 * ```ts
 * const formatKr = createAmountFormatter({ suffix: 'kr' });
 * formatKr(1000); // '1 000 kr'
 * formatKr(1000, { signed: true }); // '+ 1 000 kr'
 * formatKr(1000.5, { decimals: 2 }); // '1 000,50 kr'
 * ```
 */
export const createAmountFormatter =
  (defaults: FormatAmountOptions = {}) =>
  (value: number, options: FormatAmountOptions = {}) =>
    formatAmount(value, { ...defaults, ...options });
